import { useEffect } from 'react';
import { AppShell, Box, Group, Text } from '@mantine/core';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { tokens as T } from '../theme/tokens';
import { useUIStore } from '../stores/uiStore';
import { useEstatesStore, useTenantsStore } from '../stores/dataStores';
import { rowsOf } from '../stores/collection';

const CONTENT_PAD_X = 30;

/** The scoped-into banner is full-bleed above the content padding, which is why
 *  the shell pads its own inner Box rather than letting AppShell do it. */
function ScopeBanner() {
  const scope = useUIStore((s) => s.scope);
  const setScope = useUIStore((s) => s.setScope);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  if (!scope) return null;
  const t = tenants.find((x) => x.key === scope);
  return (
    <Group
      justify="space-between"
      gap={16}
      wrap="wrap"
      px={CONTENT_PAD_X}
      py={11}
      style={{ background: T.navActive, borderBottom: `1px solid ${T.lineHard}` }}
    >
      <Text fz={13} c={T.neutralFg}>
        Scoped into{' '}
        <Text component="span" fw={800} c={T.ink}>
          {t?.displayName ?? scope}
        </Text>{' '}
        — pool selection, usage and tokens below are filtered to this tenant. Estate-wide
        credentials stay read-only.
      </Text>
      <Text
        fz={12}
        fw={800}
        c={T.coralDeep}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={() => setScope(null)}
      >
        Exit tenant ✕
      </Text>
    </Group>
  );
}

export function Shell({ preview }: { preview?: boolean }) {
  // The sidebar lists estates on every screen, so the watch belongs to the
  // shell rather than to whichever page happens to want them too. Opening it
  // twice is harmless — the second open replaces the first generation.
  const openEstates = useEstatesStore((s) => s.open);
  // Tenants are shell-level for the same reason: the scope picker in the header
  // lists them on every screen, and the estate cards count them.
  const openTenants = useTenantsStore((s) => s.open);
  useEffect(() => {
    openEstates(undefined as void);
    openTenants(undefined);
  }, [openEstates, openTenants]);

  return (
    <AppShell
      header={{ height: 54 }}
      navbar={{ width: 262, breakpoint: 'sm' }}
      padding={0}
      styles={{
        main: { background: T.page, minHeight: '100vh' },
        navbar: { background: T.sidebar, border: 'none' },
        header: { background: T.page, borderBottom: 'none' },
      }}
    >
      <AppShell.Header>
        <Header preview={preview} />
      </AppShell.Header>
      <AppShell.Navbar p={0}>
        <Sidebar />
      </AppShell.Navbar>
      <AppShell.Main>
        <ScopeBanner />
        <Box
          pt={26}
          px={CONTENT_PAD_X}
          pb={60}
          style={{ ['--rt-content-pad' as string]: `${CONTENT_PAD_X}px` }}
        >
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
